/**
 * Shared constants for the corporate structure UI — icons, colors, metadata.
 */

import {
  Building2,
  User,
  Landmark,
  HelpCircle,
  Users,
  PieChart,
  Network,
  Shield,
  Tag,
  Brain,
  Link2,
  Scale,
} from "lucide-react";
import type { BadgeColor } from "@/ui/primitives/badge";

// ---------------------------------------------------------------------------
// Entity type icons & colors (shared across CorporateStructureView + StructureMap)
// ---------------------------------------------------------------------------

export const TYPE_ICON: Record<string, typeof Building2> = {
  company: Building2,
  person: User,
  trust: Landmark,
  organisation: Building2,
  unknown: HelpCircle,
};

/** Flat color string used in CorporateStructureView data table */
export const TYPE_COLOR: Record<string, string> = {
  company:
    "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-950/30 dark:border-blue-800",
  person:
    "text-violet-600 bg-violet-50 border-violet-200 dark:text-violet-400 dark:bg-violet-950/30 dark:border-violet-800",
  trust:
    "text-amber-600 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-950/30 dark:border-amber-800",
  organisation:
    "text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-800",
  unknown:
    "text-gray-500 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-950/30 dark:border-gray-800",
};

/** Structured color object used in StructureMap */
export const STRUCTURE_MAP_TYPE_COLORS: Record<
  string,
  { bg: string; border: string; text: string }
> = {
  company: {
    bg: "bg-blue-50 dark:bg-blue-950/40",
    border: "border-blue-200 dark:border-blue-800",
    text: "text-blue-700 dark:text-blue-300",
  },
  person: {
    bg: "bg-violet-50 dark:bg-violet-950/40",
    border: "border-violet-200 dark:border-violet-800",
    text: "text-violet-700 dark:text-violet-300",
  },
  trust: {
    bg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-200 dark:border-amber-800",
    text: "text-amber-700 dark:text-amber-300",
  },
  organisation: {
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-200 dark:border-emerald-800",
    text: "text-emerald-700 dark:text-emerald-300",
  },
};

// ---------------------------------------------------------------------------
// Group category metadata (CorporateStructureView)
// ---------------------------------------------------------------------------

export const GROUP_CATEGORY_META: Record<
  string,
  { icon: typeof Building2; color: string; label: string }
> = {
  officers: {
    icon: Users,
    color: "text-violet-600 dark:text-violet-400",
    label: "Officers & Directors",
  },
  shareholders: {
    icon: PieChart,
    color: "text-sky-600 dark:text-sky-400",
    label: "Shareholders",
  },
  trusts: {
    icon: Landmark,
    color: "text-amber-600 dark:text-amber-400",
    label: "Trusts",
  },
  secured_parties: {
    icon: Shield,
    color: "text-amber-600 dark:text-amber-400",
    label: "Secured Parties",
  },
  professionals: {
    icon: Scale,
    color: "text-slate-600 dark:text-slate-400",
    label: "Professionals",
  },
  related_companies: {
    icon: Link2,
    color: "text-emerald-600 dark:text-emerald-400",
    label: "Related Entities",
  },
  subsidiaries: {
    icon: Building2,
    color: "text-blue-600 dark:text-blue-400",
    label: "Subsidiaries",
  },
  other: {
    icon: HelpCircle,
    color: "text-gray-500 dark:text-gray-400",
    label: "Other",
  },
};

// ---------------------------------------------------------------------------
// Sub-agent icon & color maps (CorporateSubAgentPanel + UnifiedTimelinePanel)
// ---------------------------------------------------------------------------

export const SUB_AGENT_ICONS: Record<string, typeof Brain> = {
  primary_officers: Users,
  shareholders: PieChart,
  structure: Network,
  secured_professionals: Shield,
  business_other: Tag,
  synthesizer: Brain,
};

export const SUB_AGENT_BORDER_COLORS: Record<string, string> = {
  primary_officers: "border-l-violet-500",
  shareholders: "border-l-sky-500",
  structure: "border-l-blue-500",
  secured_professionals: "border-l-amber-500",
  business_other: "border-l-slate-400",
  synthesizer: "border-l-emerald-500",
};

export const SUB_AGENT_ICON_COLORS: Record<string, string> = {
  primary_officers:
    "text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-950/30",
  shareholders: "text-sky-600 bg-sky-50 dark:text-sky-400 dark:bg-sky-950/30",
  structure: "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/30",
  secured_professionals:
    "text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30",
  business_other:
    "text-slate-600 bg-slate-50 dark:text-slate-400 dark:bg-slate-950/30",
  synthesizer:
    "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/30",
};

// ---------------------------------------------------------------------------
// Timeline constants
// ---------------------------------------------------------------------------

export const MAX_SLOTS = 7;

export const MAIN_ICON_COLOR =
  "text-primary bg-primary/10 dark:text-primary dark:bg-primary/20";

export const SUB_AGENT_BADGE_COLOR: Record<string, BadgeColor> = {
  primary_officers: "violet",
  shareholders: "sky",
  structure: "blue",
  secured_professionals: "amber",
  business_other: "gray",
  synthesizer: "emerald",
};
